import { Observable } from "rxjs/Observable";
import { compose, createEventHandler } from "recompose";
import trackEvent from "theme/modules/Analytics/trackEvent";
import { ApolloError } from "apollo-client";
import { withApollo } from "react-apollo";
import mapPropsStream from "../utils/mapPropsStream";

export const NETWORK_ERROR_MESSAGE =
  "A network error occured, plese check your internet connection.";
export const GENERIC_ERROR_MESSAGE =
  "An unexpected error occured, please check sent values.";

const apolloMutationToRxObservable = (apolloPromise) => {
  return Observable.create((observer) => {
    apolloPromise
      .then((response) =>
        observer.next({ status: "success", response: response })
      )
      .catch((error) => {
        let response = error;
        if (error instanceof ApolloError) {
          console.error(Object.keys(error), error.networkError);
          if (process.env.NODE_ENV !== "production") {
            console.error("Mutation error", error);
          }
          response = {
            success: false,
            data: error.networkError
              ? NETWORK_ERROR_MESSAGE
              : GENERIC_ERROR_MESSAGE,
          };
        }
        observer.next({ status: "error", response });
      });
  });
};

export const makeCommandDispatcherStream = (handlers) => (props$) => {
  const commands = Object.keys(handlers)
    .map((handlerName) => ({
      handlerName,
      handler: handlers[handlerName],
    }))
    .map(({ handlerName, handler }) => {
      const { handler: dispatchCommand, stream: dispatchCommand$ } =
        createEventHandler();

      return {
        commandName: handlerName,
        dispatchCommand,
        stream: dispatchCommand$
          .withLatestFrom(props$, (data, props) => ({ props, data }))
          .map(({ props, data }) => handler(props)(data)),
      };
    });

  const dispatchCommand$ = Observable.merge(
    ...commands.map(({ stream }) => stream)
  );

  const mutationResponse$ = dispatchCommand$
    .filter(({ preconditionFailed }) => !preconditionFailed)
    .withLatestFrom(
      props$,
      ({ options, messages, callback }, { client, onSuccess, onError }) => ({
        client,
        options,
        messages,
        callback,
        onSuccess,
        onError,
      })
    )
    .do(({ options }) =>
      trackEvent(options.mutation.definitions[0].name.value, {
        category: "Mutations",
      })
    )
    .switchMap(
      ({ client, route, options, messages, callback, onSuccess, onError }) => {
        return apolloMutationToRxObservable(client.mutate(options))
          .map(({ status, response }) => {
            const operationName = options.mutation.definitions[0].name.value;
            const data = response.data[operationName]
              ? response.data[operationName]
              : response.data;

            return {
              status:
                status === "success" && data.success ? "success" : "error",
              data: data,
            };
          })
          .do(({ status, data }) => {
            if (status === "success") {
              onSuccess && onSuccess(data);
            } else {
              onError && onError(data);
            }
            callback && callback({ status, data });
          })
          .map(({ data, status }) => {
            messages = messages || {
              success: "",
              error: data.errorMessage || "",
            };

            return {
              success: status === "success",
              response: data,
              commandPending: false,
              message: status === "success" ? messages.success : messages.error,
            };
          })
          .startWith({
            commandPending: true,
            response: undefined,
            message: "",
          });
      }
    )
    .startWith({ commandPending: false, response: undefined, message: "" });

  const preconditionFailedMessages$ = dispatchCommand$
    .filter(({ preconditionFailed }) => preconditionFailed)
    .map(({ preconditionFailed }) => ({
      success: false,
      commandPending: false,
      hasMessage: true,
      message: preconditionFailed,
    }));

  const commandResponse$ = Observable.merge(
    mutationResponse$,
    preconditionFailedMessages$
  );

  return props$.combineLatest(
    commandResponse$,
    (props, { commandPending, response, message, success }) => ({
      ...props,
      success,
      hasMessage: !!message,
      message: message,
      commandResponse: response,
      commandPending,
      ...commands.reduce(
        (obj, { commandName, dispatchCommand }) => ({
          ...obj,
          [commandName]: dispatchCommand,
        }),
        {}
      ),
    })
  );
};

export default (handlers) =>
  compose(withApollo, mapPropsStream(makeCommandDispatcherStream(handlers)));
